import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { logger } from './logger';
import { applyRemoteMedicationEntries, type MedicationInfo } from '../utils/medicationDatabase';
import {
  MEDICATION_CATALOG_COLLECTION,
  MEDICATION_CATALOG_DOC_ID
} from '../constants/medicationCatalog';

const log = logger.createScope('MedicationCatalogService');

// Re-export path for importers that only need the collection/doc ids
export { MEDICATION_CATALOG_COLLECTION, MEDICATION_CATALOG_DOC_ID };

export const medicationCatalogRef = () => doc(db, MEDICATION_CATALOG_COLLECTION, MEDICATION_CATALOG_DOC_ID);

/**
 * Firestore document shape. iOS: listen to the same path with the Firebase iOS SDK.
 * `entries` is a map: normalized medication name (lowercase key) → {@link MedicationInfo}.
 */
export interface MedicationCatalogDocument {
  schemaVersion: number;
  /** Bumped when you republish; clients may use for cache invalidation. */
  version: number;
  entryCount: number;
  /** Full merged catalog from last publish (remote overrides app fallback for overlapping keys). */
  entries: Record<string, MedicationInfo>;
  updatedAt?: { toMillis?: () => number } | null;
}

/**
 * Subscribes to the catalog document and merges `entries` into the in-memory database used by
 * {@link getMedicationInfo} / {@link searchMedications}. Unsubscribe on cleanup.
 * If the document is missing, falls back to the shipped database only.
 */
export function subscribeToMedicationCatalog(): () => void {
  const ref = medicationCatalogRef();
  const unsub = onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        log.debug('Medication catalog document missing; using app fallback only');
        applyRemoteMedicationEntries(null);
        return;
      }
      const data = snap.data() as Partial<MedicationCatalogDocument>;
      const entries = data.entries;
      if (entries && typeof entries === 'object' && !Array.isArray(entries)) {
        const count = Object.keys(entries).length;
        log.info('Medication catalog loaded from Firestore', {
          version: data.version,
          schemaVersion: data.schemaVersion,
          entryCount: count
        });
        applyRemoteMedicationEntries(entries as Record<string, MedicationInfo>);
      } else {
        log.warn('Medication catalog document has no valid entries map; using fallback only');
        applyRemoteMedicationEntries(null);
      }
    },
    (err) => {
      log.warn('Medication catalog listener error; using app fallback', { error: (err as Error).message });
    }
  );
  return unsub;
}
